/*
 * Dr.Paw Brand Palette
 * ─────────────────────────────────────
 * Beige:  #CBB8A8   Brown:  #BFA78F
 * Green:  #6F8376   Orange: #D89A7A
 * Salmon: #CFA99C
 * ─────────────────────────────────────
 */

export const PALETTE = {
  beige:  "#CBB8A8",
  brown:  "#BFA78F",
  green:  "#6F8376",
  orange: "#D89A7A",
  salmon: "#CFA99C",
  // Derived tones
  greenDark:    "#3A4840",
  greenDeep:    "#2D3832",
  brownDark:    "#A8876F",
  warmBlack:    "#1A1614",
  warmCharcoal: "#231F1C",
  warmDark:     "#3D3630",
  warmMuted:    "#7A6E64",
  creamBg:      "#F7F3EF",
  creamLight:   "#FAF7F4",
  white:        "#FFFFFF",
  black:        "#000000",
} as const;

export const COLORS = {
  // Base Colors
  background: "var(--color-background)",
  backgroundLighter: "var(--color-background-lighter)",
  backgroundDarker: "var(--color-background-darker)",

  // Primary Palette
  primary: "var(--color-primary)",
  primaryLighter: "var(--color-primary-lighter)",
  primaryDarker: "var(--color-primary-darker)",
  primaryTransparent: "var(--color-primary-transparent)",
  primaryTransparentLight: "var(--color-primary-transparent-light)",

  // Text Colors
  textMain: "var(--color-text-main)",
  textMuted: "var(--color-text-muted)",
  textContrast: "var(--color-text-contrast)",

  // UI Elements
  border: "var(--color-border)",
  borderLight: "var(--color-border-light)",
  borderHover: "var(--color-border-hover)",
  glass: "var(--color-glass)",
  overlay: "var(--color-overlay)",

  // Effects
  shadow: "var(--color-shadow)",
  glow: "var(--color-glow)",

  // Constant Colors
  white: PALETTE.white,
  black: PALETTE.black,
};

export type ThemeType = "original" | "midnight" | "emerald" | "sunset";

export const THEMES: Record<ThemeType, Record<string, string>> = {
  /* ── Original — warm dark with Orange primary ────────────── */
  original: {
    "--color-background": PALETTE.warmBlack,
    "--color-background-lighter": PALETTE.warmCharcoal,
    "--color-background-darker": "#120F0D",
    "--color-primary": PALETTE.orange,           // #ef9466
    "--color-primary-lighter": PALETTE.salmon,   // #CFA99C
    "--color-primary-darker": PALETTE.brown,     // #BFA78F
    "--color-primary-transparent": "rgba(216, 154, 122, 0.1)",
    "--color-primary-transparent-light": "rgba(216, 154, 122, 0.05)",
    "--color-text-main": "#F5F0EB",
    "--color-text-muted": "rgba(216, 154, 122, 0.6)",
    "--color-text-contrast": PALETTE.warmBlack,
    "--color-border": "rgba(216, 154, 122, 0.12)",
    "--color-border-light": "rgba(216, 154, 122, 0.18)",
    "--color-border-hover": "rgba(216, 154, 122, 0.4)",
    "--color-glass": "rgba(35, 31, 28, 0.65)",
    "--color-overlay": "rgba(35, 31, 28, 0.85)",
    "--color-shadow": "rgba(216, 154, 122, 0.2)",
    "--color-glow": "rgba(216, 154, 122, 0.3)",
  },
  /* ── Midnight — cool dark with Green accent ──────────────── */
  midnight: {
    "--color-background": "#0F1412",
    "--color-background-lighter": "#1A211E",
    "--color-background-darker": "#080C0A",
    "--color-primary": PALETTE.green,            // #6F8376
    "--color-primary-lighter": PALETTE.beige,    // #CBB8A8
    "--color-primary-darker": PALETTE.greenDark, // #3A4840
    "--color-primary-transparent": "rgba(111, 131, 118, 0.1)",
    "--color-primary-transparent-light": "rgba(111, 131, 118, 0.05)",
    "--color-text-main": "#F5F0EB",
    "--color-text-muted": "rgba(111, 131, 118, 0.6)",
    "--color-text-contrast": "#F5F0EB",
    "--color-border": "rgba(111, 131, 118, 0.1)",
    "--color-border-light": "rgba(111, 131, 118, 0.15)",
    "--color-border-hover": "rgba(111, 131, 118, 0.4)",
    "--color-glass": "rgba(26, 33, 30, 0.65)",
    "--color-overlay": "rgba(26, 33, 30, 0.85)",
    "--color-shadow": "rgba(111, 131, 118, 0.2)",
    "--color-glow": "rgba(111, 131, 118, 0.3)",
  },
  /* ── Emerald — deep green bg with Salmon accent ──────────── */
  emerald: {
    "--color-background": PALETTE.greenDeep,
    "--color-background-lighter": PALETTE.greenDark,
    "--color-background-darker": "#1E2A24",
    "--color-primary": PALETTE.salmon,           // #CFA99C
    "--color-primary-lighter": PALETTE.beige,    // #CBB8A8
    "--color-primary-darker": PALETTE.brown,     // #BFA78F
    "--color-primary-transparent": "rgba(207, 169, 156, 0.1)",
    "--color-primary-transparent-light": "rgba(207, 169, 156, 0.05)",
    "--color-text-main": "#F5F0EB",
    "--color-text-muted": "rgba(207, 169, 156, 0.6)",
    "--color-text-contrast": PALETTE.greenDeep,
    "--color-border": "rgba(207, 169, 156, 0.1)",
    "--color-border-light": "rgba(207, 169, 156, 0.15)",
    "--color-border-hover": "rgba(207, 169, 156, 0.4)",
    "--color-glass": "rgba(58, 72, 64, 0.65)",
    "--color-overlay": "rgba(58, 72, 64, 0.85)",
    "--color-shadow": "rgba(207, 169, 156, 0.2)",
    "--color-glow": "rgba(207, 169, 156, 0.3)",
  },
  /* ── Sunset — warm brown bg with Beige/Orange accent ─────── */
  sunset: {
    "--color-background": "#1E1714",
    "--color-background-lighter": "#2E241F",
    "--color-background-darker": "#120D0B",
    "--color-primary": PALETTE.beige,            // #CBB8A8
    "--color-primary-lighter": PALETTE.salmon,   // #CFA99C
    "--color-primary-darker": PALETTE.brownDark, // #A8876F
    "--color-primary-transparent": "rgba(203, 184, 168, 0.1)",
    "--color-primary-transparent-light": "rgba(203, 184, 168, 0.05)",
    "--color-text-main": "#F5F0EB",
    "--color-text-muted": "rgba(203, 184, 168, 0.6)",
    "--color-text-contrast": "#F5F0EB",
    "--color-border": "rgba(203, 184, 168, 0.1)",
    "--color-border-light": "rgba(203, 184, 168, 0.15)",
    "--color-border-hover": "rgba(203, 184, 168, 0.4)",
    "--color-glass": "rgba(46, 36, 31, 0.65)",
    "--color-overlay": "rgba(46, 36, 31, 0.85)",
    "--color-shadow": "rgba(203, 184, 168, 0.2)",
    "--color-glow": "rgba(203, 184, 168, 0.3)",
  },
};
